import { IBlogIndexEntry, IBlogPost } from '@/interfaces';
import { getAuthorImage } from './authors';

// Client-safe blog utility functions
// These don't use fs/path and can be imported in client components

// Helper function to parse tags string to array
export function parseBlogTags(tags: string | null): string[] {
  if (!tags) return [];
  return tags
    .split(',')
    .map(tag => tag.trim())
    .filter(tag => tag.length > 0);
}

// Helper function to get title based on language
export function getBlogTitle(post: IBlogPost, language: 'en' | 'zh'): string {
  if (language === 'en') {
    return (
      post.custom_feature_blog_title_en ||
      post.custom_feature_blog_title_cn ||
      ''
    );
  }
  return (
    post.custom_feature_blog_title_cn || post.custom_feature_blog_title_en || ''
  );
}

// Helper function to get content based on language
export function getBlogContent(post: IBlogPost, language: 'en' | 'zh'): string {
  if (language === 'en') {
    return (
      post.custom_feature_blog_text_en || post.custom_feature_blog_text_cn || ''
    );
  }
  return (
    post.custom_feature_blog_text_cn || post.custom_feature_blog_text_en || ''
  );
}

// Helper function to get tags based on language
export function getBlogTags(post: IBlogPost, language: 'en' | 'zh'): string[] {
  if (language === 'en') {
    return (
      parseBlogTags(post.custom_feature_blog_tags_en) ||
      parseBlogTags(post.custom_feature_blog_tags_cn)
    );
  }
  return (
    parseBlogTags(post.custom_feature_blog_tags_cn) ||
    parseBlogTags(post.custom_feature_blog_tags_en)
  );
}

// Helper function to get category based on language
export function getBlogCategory(
  post: IBlogPost,
  language: 'en' | 'zh'
): string | undefined {
  if (language === 'en') {
    return post.blog_category_name_en || post.blog_category_name_cn;
  }
  return post.blog_category_name_cn || post.blog_category_name_en;
}

// Helper function to get excerpt based on language
export function getBlogExcerpt(post: IBlogPost, language: 'en' | 'zh'): string {
  if (language === 'en') {
    if (
      post.custom_feature_blog_excerpt_en ||
      post.custom_feature_blog_excerpt_cn
    ) {
      return (
        post.custom_feature_blog_excerpt_en ||
        post.custom_feature_blog_excerpt_cn
      );
    }
  } else {
    if (
      post.custom_feature_blog_excerpt_cn ||
      post.custom_feature_blog_excerpt_en
    ) {
      return (
        post.custom_feature_blog_excerpt_cn ||
        post.custom_feature_blog_excerpt_en
      );
    }
  }
  // Generate excerpt from content if not provided
  const content = getBlogContent(post, language);
  const textContent = content.replace(/<[^>]*>/g, '');
  return textContent.slice(0, 200) + (textContent.length > 200 ? '...' : '');
}

// Helper function to get author
export function getBlogAuthor(post: IBlogPost): string {
  return post.custom_feature_blog_extra_json.author;
}

// Helper function to get author profile picture using centralized lookup
export function getBlogAuthorProfilePicture(post: IBlogPost): string {
  const author = getBlogAuthor(post);
  return getAuthorImage(author);
}

// Helper function to get read count
export function getBlogReadCount(post: IBlogPost): number {
  return post.custom_feature_blog_extra_json?.read_count || 0;
}

// Helper function to get blog date for display
export function getBlogDate(post: IBlogPost): string {
  return post.custom_feature_blog_date;
}

// Format date for display
export function formatDate(
  dateString: string,
  locale: 'en' | 'zh' = 'en'
): string {
  const date = new Date(dateString);

  if (locale === 'zh') {
    return date.toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

// Helper functions for IBlogIndexEntry (lightweight index)
export function getIndexTitle(
  post: IBlogIndexEntry,
  language: 'en' | 'zh'
): string {
  return language === 'en' ? post.title_en : post.title_cn;
}

export function getIndexCategory(
  post: IBlogIndexEntry,
  language: 'en' | 'zh'
): string {
  return language === 'en' ? post.category_en : post.category_cn;
}

export function getIndexExcerpt(
  post: IBlogIndexEntry,
  language: 'en' | 'zh'
): string {
  return language === 'en' ? post.excerpt_en : post.excerpt_cn;
}

export function getIndexTags(
  post: IBlogIndexEntry,
  language: 'en' | 'zh'
): string[] {
  return language === 'en' ? post.tags_en : post.tags_cn;
}

export function getIndexReadingTime(
  post: IBlogIndexEntry,
  language: 'en' | 'zh'
): string {
  return language === 'en' ? post.reading_time_en : post.reading_time_cn;
}
